import { Document } from '@contentful/rich-text-types';
import { Asset, Entry, ReferencedEntry } from 'contentful';
import { FormEndpointType } from 'src/components/Contact/FormEndpoints';
import { getContentType } from 'src/utils/CloverdotcomEntryUtil';
import { Entries, Field } from './utilTypes';

export enum ContentType {
  ACCORDION_ASSEMBLY = 'accordionAssembly',
  AD_AGENT = 'adAgent',
  ARTICLE_ASSEMBLY = 'articleAssembly',
  BANNER_MODULE = 'bannerComponent',
  BANNER_ASSEMBLY = 'bannerAssembly',
  BASIC_PAGE = 'page',
  BUNDLE = 'bundle',
  BUNDLE_FEATURE = 'bundleFeature',
  BUNDLE_FEATURE_CATEGORY = 'bundleFeatureCategory',
  BUNDLE_FEATURE_VALUE = 'bundleFeatureValue',
  BUNDLE_SUMMARY_MODULE = 'bundleSummaryModule',
  BUSINESS_RESOURCE_GRID = 'businessResourceGrid',
  CARD_ASSEMBLY = 'cardAssembly',
  CAROUSEL_ASSEMBLY = 'carouselAssembly',
  COMPONENT_ASSEMBLY = 'componentAssembly',
  CONTROLLED_SCROLL_ASSEMBLY = 'controlledScrollAssembly',
  CONTROLLED_SCROLL_ITEM_ASSEMBLY = 'controlledScrollItemAssembly',
  DYNAMIC_ASSEMBLY = 'dynamicAssembly',
  ENTRY_SET = 'set',
  EXTERNAL_LINK = 'externalLink',
  FAQ_MODULE = 'faqModule',
  FEATURE_COMPARISON_ASSEMBLY = 'featureComparisonAssembly',
  FEATURE = 'feature',
  FEATURE_FLAG = 'featureFlag',
  FLOATING_TEXT_ASSEMBLY = 'floatingAssembly',
  FOOTER = 'footer',
  GRID_ASSEMBLY = 'gridAssembly',
  HERO_MODULE = 'heroModule',
  ICON_FEATURE_GRID = 'iconFeatureGrid',
  IMAGE_TEXT_MODULE = 'imageTextModule',
  INTERNAL_LINK = 'internalLink',
  LEAD_GEN_FORM_MODULE = 'leadGenFormModule',
  LINK_SET = 'linkSet',
  MEDIA = 'media',
  MEDIA_MODULE = 'mediaAssembly',
  MODAL_ASSEMBLY = 'modalModule',
  MODAL_LINK = 'modalLink',
  MULTIPANEL_MODULE = 'multiPanelModule',
  NAVIGATION = 'navigation',
  PLAIN_TEXT_MODULE = 'plainTextAssembly',
  PRICING_DETAIL_MODULE = 'pricingDetailModule',
  PRODUCT = 'product',
  PRODUCT_MODULE = 'productModule',
  PRODUCT_TILE_ASSEMBLY = 'productTileAssembly',
  QUOTES_MODULE = 'quotesAssembly',
  SALES_CONTACT_MODULE = 'salesContactModule',
  SINGLE_TESTIMONIAL_MODULE = 'singleTestimonialModule',
  SOFTWARE_PLAN = 'softwarePlan',
  SOLUTION = 'solution',
  SOLUTION_PICKER_MODULE = 'solutionPickerModule',
  STICKY_HEADER_ASSEMBLY = 'stickyHeaderAssembly',
  SUBJECT = 'subject',
  SUB_NAVIGATION = 'subNavigation',
  TABS_ASSEMBLY = 'tabsAssembly',
  TAB_ITEM = 'tabItem',
  TESTIMONIAL_MODULE = 'testimonialModule',
  VERTICAL_MARKET = 'vertical',
  SIDE_BY_SIDE_MODULE = 'sideBySideDeviceModule',
  NAV_SUB_ITEM = 'navSubItem',
  PRODUCT_SYSTEMS_CATALOG = 'productSystemsCatalog',
}

export function is<T>(contentType: ContentType, maybe?: Entry<any>): maybe is Entry<T>;
export function is<T>(contentType: ContentType, maybe?: ReferencedEntry<any>): maybe is ReferencedEntry<T>;
export function is<T>(contentType: ContentType, maybe?: Entry<any> | ReferencedEntry<any>): maybe is Entry<T> | ReferencedEntry<T> {
  return getContentType(maybe) === contentType;
}

export function as<T>(contentType: ContentType, entry?: Entry<any>): Entry<T> | undefined;
export function as<T>(contentType: ContentType, entry?: ReferencedEntry<any>): ReferencedEntry<T> | undefined;
export function as<T>(contentType: ContentType, entry?: Entry<any> | ReferencedEntry<any>): Entry<T> | ReferencedEntry<T> | undefined {
  return is<T>(contentType, entry) && entry || undefined;
}

// ******** special attribute enums ******** //

export enum CarouselType {
  FEATURE = 'Feature',
  SQUARE = 'Square',
  HERO = 'Hero',
  PRODUCT = 'Product',
}

export enum DynamicType {
  CAREERS = 'careers',
  SHOPPING_FOOTER = 'shopping-footer',
  JOB_POST = 'job-post',
}

export enum NavigationType {
  FOOTER = 'footer',
  HEADER = 'header',
  HEADER_V2 = 'header-v2',
  HEADER_UTIL = 'header-util',
  HEADER_UTIL_V2 = 'header-util-v2',
  SIDEBAR = 'sidebar',
  SIDEBAR_SALES_HELP = 'sidebar-sales-help',
  SIDEBAR_OVERRIDE = 'sidebar-override',
}

export enum ImageAlignment {
  LEFT = 'Left',
  RIGHT = 'Right',
}

export enum ImageMobileAlignment {
  TOP = 'Top',
  BOTTOM = 'Bottom',
}

export enum TextAlignment {
  LEFT = 'Left',
  CENTER = 'Center',
  RIGHT = 'Right',
  TOP = 'Top',
}

export enum TextMediaModuleType {
  HEADLINE = 'Headline',
}

/* this enum can be used in the future to add other media source types(like youtube, contentful asset) */
export enum MediaSourceType {
  VIMEO = 'Vimeo',
}

export enum Weight {
  LIGHT = 'Light',
  REGULAR = 'Regular',
  BOLD = 'Bold',
}

export enum ContentfulPadding {
  NONE = 'None',
  SMALL = 'Small',
  MEDIUM = 'Medium',
  LARGE = 'Large',
}

export enum ContentfulMargin {
  NONE = 'None',
}

export enum LinkVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TEXT = 'text',
}

export enum BundleFeatureValueVariant {
  Included = 'Included',
  Image = 'Image',
  OnlyText = 'Only Text',
  Product = 'Product',
  Pricing = 'Pricing',
  Link = 'Link',
}

export enum CardsArrangement {
  WITH_SPACE = 'With Space',
  WITHOUT_SPACE = 'Without Space',
}

export enum PricingTableFeatureType {
  PRODUCT_MODAL = 'Product Modal',
  LINK = 'Link',
}

// ******** common fields ******** //

export type HasBackgroundColor = Field<{ backgroundColor: string }>;

export type HasColor = Field<{ color: string }>;

export type HasSlug = Field<{ slug: string }>;

export type HasTitle = Field<{ title: string; titleColor: string }>;

export type HasSubTitle = Field<{ subTitle: string; subTitleColor: string }>;

export type HasHint = Field<{ hint: string }>;

export type HasInternalName = Field<{ internalName: string }>;

export type HasBlurb = Field<{ blurb: string; blurbColor: string }>;

export type HasTextAlignment = Field<{ textAlignment: TextAlignment }>;

export type HasImage = Field<{ image: Asset }>;

export type HasImages = Field<{ images: Asset[] }>;

export type HasMobileImage = Field<{ mobileImage: Asset }>;

export type HasVideo = Field<{ video: Asset }> & HasImage;

export type HasLink = Field<{ link: ReferencedEntry<Link> }>;

export type HasCtaButtonOption = Field<{ isCtaButton: boolean }>;

export type HasCarouselType = Field<{ carouselType: CarouselType }>;

export type HasMaterialIcon = Field<{ materialIcon: string }>;

export type HasModuleType = Field<{ moduleType: TextMediaModuleType }>;

export type HasTextBoxAlignment = Field<{ textBoxAlignment: TextAlignment.LEFT | TextAlignment.RIGHT }>;

export type HasExternalSources = Field<{ youtubeId?: string }>;

export type HasAmbient = Field<{ isAmbient?: boolean }>;

export type HasMedia = Field<{ media?: ReferencedEntry<MediaFields> }>;

export type HasDisallowedResellers = Field<{ disallowedResellers?: ReadonlyArray<string> }>;

export type HasLinkVariant = Field<{ variant?: LinkVariant }>;

export type FontWeight = Field<{ fontWeight: Weight.LIGHT | Weight.REGULAR | Weight.BOLD }>;

export type HasDescription = Field<{ description: string }>;

export type HasDcsId = Field<{ dcsId: string }>;

export type HasKey = Field<{ key: string }>;

// ******** org ******** //

export type Topic = Product | Subject;

export type Link = InternalLink | ExternalLink | ModalLink | Page | Product | NavSubItem;

export type Assembly = AccordionAssembly
  & CarouselAssembly
  & BannerAssembly
  & CardAssembly
  & ControlledScrollAssembly
  & FaqModule
  & FloatingTextAssembly
  & GridAssembly
  & MultiPanelModule
  & ProductTileAssembly
  & TabsAssembly
  & QuotesAssembly
  & TestimonialAssembly
  & DynamicAssembly
  & FeatureComparisonAssembly
  & SolutionPickerModule
  & StickyHeaderAssembly
  & ProductSystemsCatalog
  & SideBySideAssembly
  & HeroModule;

// ******** topic content types ******** //

export type Subject = HasInternalName & HasBlurb & HasTitle & HasImage & HasMobileImage & HasVideo & HasLink & HasExternalSources & HasAmbient & HasMedia & HasBackgroundColor;

export type VerticalMarket = HasTitle & HasImage & HasVideo & Field<{
  primaryColor: string;
  accentColor: string;
}>;

export type Faq = HasInternalName & Field<{
  question: string;
  answer: string;
}>;

export type Product = HasDcsId & Subject & Field<{
  firstDataMarketplaceId: string;
  msrp?: number;
}>;

export type ExternalLink = HasTitle & HasHint & HasMaterialIcon & HasDisallowedResellers & HasLinkVariant & HasImage & Field<{
  url: string;
  queryParameters: any;
  newTab: boolean;
}>;

export type InternalLink = HasTitle & HasHint & HasMaterialIcon & HasDisallowedResellers & HasLinkVariant & HasImage & Field<{
  page: ReferencedEntry<Page>;
  queryParameters: any;
}>;

export type ModalLink = HasTitle & HasHint & HasLinkVariant & Field<{
  modal: ReferencedEntry<ModalAssembly>;
  hideButton: boolean;
}>;

export type TabItem = HasTitle & HasInternalName & Field<{
  module: ReferencedEntry<MediaAssembly | CardAssembly | AccordionAssembly>;
}>;

export type NavSubItem = HasTitle & HasBlurb & HasInternalName & HasLink & Field<{
  subItemLinks: Entries<HasLink>;
}>;

export type Footer = HasInternalName & HasBackgroundColor & Field<{
  primaryNavItems: Entries<LinkSet>;
  primaryBackgroundColor: string;
  primaryBackgroundImage: Asset;
  primaryBackgroundSmallImage: Asset;
  primaryFontSize: number;
  primaryMobileFontSize: number;
  primaryFontColor: string;
  secondaryNavItems: Entries<LinkSet>;
  secondaryFontSize: number;
  secondaryFontColor: string;
  socialLinks: Entries<ExternalLink>;
  legalLinks: Entries<ExternalLink | InternalLink>;
  legalFontSize: number;
  legalFontColor: string;
  localePickerFontSize: number;
  localePickerFontColor: string;
}>;

export enum FormFields {
  FIRST_AND_LAST_NAME = 'First and Last Name',
  EMAIL = 'Email Address',
  BUSINESS_NAME = 'Business Name',
  PHONE_NUMBER = 'Phone Number',
  MOBILE_PHONE_NUMBER = 'Mobile Phone Number',
  ZIP_CODE = 'ZIP Code',
  BUSINESS_ADDRESS = 'Business Address',
  BUSINESS_TYPE = 'Business Type',
  YEARS_IN_BUSINESS = 'Years in Business',
  SSN_LAST_4 = 'SSN Last 4',
  BANK_ACCOUNT_LAST_4 = 'Bank Account Last 4',
  MID = 'MID',
  DISPLAY_TEXT = 'Display Text',
  GOOGLE_RECAPTCHA = 'Google reCAPTCHA',
  REQUIRED_CHECKBOX = 'Required Checkbox',
  ANNUAL_PROCESSING_VOLUME = 'Annual Processing Volume',
  WHEN_TO_CALL = 'When to Call',
  HIDDEN_FIELD = 'Hidden Field',
}

export type PredefinedFormField = HasKey & Field<{
  productionValue: string;
  developmentValue: string;
}>;

export type FormField = Field<{
  fieldType: FormFields;
  options?: any;
  displayDetail?: ReferencedEntry<Subject>;
  predefinedField?: ReferencedEntry<PredefinedFormField>;
}>;

// ******** assembly content types ******** //

export type Page = Set<Assembly> & HasTitle & HasSlug & HasBackgroundColor & HasDescription & Field<{
  pageMetadata: ReferencedEntry<any>;
  verticalMarket?: ReferencedEntry<VerticalMarket>;
  heroTopic: ReferencedEntry<Subject>;
  sidebar: ReferencedEntry<Navigation>;
  hideCartIcon?: boolean;
  bannerHidden?: boolean;
  header: ReferencedEntry<Navigation>;
  headerUtil: ReferencedEntry<Navigation>;
  showSpeedbump?: boolean;
  canonicalTag?: string;
  alternatingModuleColor?: string;
  referralId?: string;
  referralIdDev?: string;
  hrefLangTagList?: Document;
  subNavigationBar?: ReferencedEntry<SubNavigation>;
}>;

export type Set<T = Topic> = HasInternalName & Field<{
  items: Entries<T>;
}>;

export type AccordionAssembly = Set & HasBackgroundColor & HasTitle & HasTextAlignment & Field<{
  containsImages: boolean;
  alwaysHaveOneItemOpen: boolean;
  hero: ReferencedEntry<Topic>;
}>;

export type BundleSummaryModule = HasInternalName & HasTitle & HasLink & HasBackgroundColor & Field<{
  cardsArrangement: CardsArrangement;
  solution: ReferencedEntry<Solution>;
  showMoreCTA: string;
  showLessCTA: string;
  showCTAFontSize: number;
  bundleTitleFontSize: number;
  bundleTitleFontColor: string;
  bundlePricingTitleFontSize: number;
  bundlePricingTitleFontColor: string;
  bundlePricingSubTitleFontSize: number;
  bundlePricingSubTitleFontColor: string;
  bundleDescriptionFontSize: number;
  bundleDescriptionFontColor: string;
  hasHybridFeaturesLayout: boolean;
  isPlusIconForShowMore: boolean;
  isBundleCheckoutAtBottom: boolean;
  isBundleFeaturesAlwaysOpen: boolean;
}>;

export type MediaAssembly = HasInternalName & HasBackgroundColor & HasTextAlignment & HasModuleType & Field<{
  topic: ReferencedEntry<Topic>;
  backgroundImage?: ReferencedEntry<Subject>;
  fontWeight?: FontWeight;
  bottomMargin?: ContentfulMargin;
  topPadding?: ContentfulPadding;
  bottomPadding?: ContentfulPadding;
}>;

export type PlainTextAssembly = HasInternalName & HasBackgroundColor & Field<{
  topic: ReferencedEntry<Topic>;
}>;

export type LeadGenFormAssembly = HasInternalName & HasTextAlignment & Field<{
  topic: ReferencedEntry<Topic>;
  postSubmissionTopic: ReferencedEntry<Topic>;
  formFields?: ReadonlyArray<ReferencedEntry<FormField>>;
  endpoint?: FormEndpointType;
}>;

export enum ModalType {
  MediaOnly = 'media only',
  PartnerContactForm = 'partner contact form',
  Text = 'text',
}

export type ModalAssembly = HasInternalName & HasSlug & Field<{
  type: ModalType;
  topic: ReferencedEntry<Topic>;
}>;

export type FaqModule = HasInternalName & HasTitle & HasBackgroundColor & Field<{
  items: Entries<Faq>;
}>;

export type FloatingTextAssembly = HasInternalName & HasCtaButtonOption & HasTextBoxAlignment & Field<{
  topic: ReferencedEntry<Topic>;
}>;

export type CarouselAssembly = Set<Topic> & HasTitle & HasBackgroundColor & HasTextAlignment & HasCarouselType & Field<{
  autoPlay?: boolean;
}>;

export type BannerAssembly = HasInternalName & Field<{
  topic: ReferencedEntry<Topic>;
}>;

export type CardAssembly = Set<Subject> & HasTitle & HasCtaButtonOption & Field<{
  isFloating: boolean; // If true then the top of cards will be aligned. If false then the cards will be offset.
  cardBackgroundColor: string;
}>;

export type ControlledScrollAssembly = HasInternalName & HasBackgroundColor & HasTitle & Field<{
  items: Entries<ControlledScrollItemAssembly>;
}>;

export type ControlledScrollItemAssembly = HasInternalName & HasBackgroundColor & Field<{
  isMediaMasked: boolean;
  topic: ReferencedEntry<Topic>;
}>;

export type GridAssembly = Set<Subject> & HasTitle & HasBackgroundColor & Field<{
  topic: ReferencedEntry<Topic>;
}>;

export type MultiPanelModule = Set<Subject> & HasTitle & HasBackgroundColor & Field<{
  topic: ReferencedEntry<Topic>;
}>;

export type TabsAssembly = Set<TabItem> & HasTitle & HasBackgroundColor;

export type LinkSet = Set<Link> & HasTitle & Field<{
  headerTopic: ReferencedEntry<Subject>;
  primaryLink: ReferencedEntry<Link>;
}>;

export type NavigationItem = LinkSet | Link;

export type Navigation = Set<NavigationItem> & HasTitle & HasBackgroundColor & Field<{
  type: NavigationType;
  primaryLink: ReferencedEntry<Link>;
  interactionOnHover: boolean;
}>;

export type SubNavigation = HasTitle & HasSubTitle & Field<{
  links?: Set<Link>;
  secondaryLinks?: ReferencedEntry<LinkSet>;
}>;

export type QuotesAssembly = HasBackgroundColor & HasColor & Field<{
  quote: string;
  merchantName: string;
  businessName: string;
}>;

export type TestimonialAssembly = HasInternalName & HasBackgroundColor & HasTextBoxAlignment & HasColor & HasCarouselType & Field<{
  testimony: ReferencedEntry<Subject>;
  merchant: ReferencedEntry<Subject>;
  mediaTopic: ReferencedEntry<Subject>;
  items: Entries<Subject>;
}>;

export type ArticleAssembly = HasInternalName & Field<{
  topic: ReferencedEntry<Topic>;
}>;

export type ProductTileAssembly = HasInternalName & Set<Subject> & HasTitle & Field<{
  styles?: unknown;
}>;

export type DynamicAssembly = HasInternalName & Field<{
  title: string;
  type: DynamicType;
}>;

export enum PlanIdKeys {
  PREPROD = 'PREPROD',
  PROD = 'PROD',
}

export type MediaFields = HasInternalName & Field<{
  source?: string;
  sourceType: MediaSourceType;
}>;

export type PricingDetailModule = HasInternalName & HasTitle & HasSubTitle & HasBackgroundColor & Field<{
  backLink: ReferencedEntry<Link>;
  contactText: string;
  phoneLink: ReferencedEntry<ExternalLink>;
  heroImage: Asset;
  solution: ReferencedEntry<Solution>;
}>;

export type SubscriptionPlan = HasInternalName & Field<{
  displayName: string;
  subheading: ReferencedEntry<Subject>;
  planIdConfig: {
    [ key in PlanIdKeys ]?: string
  };
}>;

export type FeatureComparisonAssembly = HasInternalName & Field<{
  items: ReadonlyArray<ReferencedEntry<SubscriptionPlan>>;
  featureTopics: ReadonlyArray<ReferencedEntry<Subject>>;
}>;

export type Feature = HasInternalName & Field<{
  product: ReferencedEntry<SubscriptionPlan>;
  value: ReferencedEntry<Subject>;
  featureTopic: ReferencedEntry<Subject>;
}>;

export type FeatureFlag = HasKey & Field<{
  production: boolean;
  staging: boolean;
  development: boolean;
  localhost: boolean;
}>;

export type SalesContactModule = HasInternalName & HasTitle & HasBlurb & HasImage & HasBackgroundColor & Field<{
  imageAlignment: ImageAlignment;
  imageMobileAlignment: ImageMobileAlignment;
  phoneTitle: string;
  phoneLink: ReferencedEntry<ExternalLink>;
  contactLink: ReferencedEntry<ExternalLink>;
}>;

export type BundleFeatureCategory = HasInternalName & HasTitle & HasSlug;

export type BundleFeature = HasInternalName & HasTitle & HasSlug & HasImage & HasDescription & Field<{
  bundleFeatureCategory: ReferencedEntry<BundleFeatureCategory>;
  product: ReferencedEntry<ProductModule>;
  variant?: string;
  link: ReferencedEntry<Link>;
}>;

export type BundleFeatureValue = HasInternalName & HasImage & Field<{
  variant: BundleFeatureValueVariant;
  included: boolean;
  text: string;
  shortText: string;
  bundleFeature: ReferencedEntry<BundleFeature>;
  product: ReferencedEntry<ProductModule>;
  link: ReferencedEntry<Link>;
}>;

export type BundleSummaryFeatureValue = HasInternalName & HasImage & HasDescription;

export type Bundle = HasInternalName & HasTitle & HasSlug & HasImage & HasLink & HasDescription & HasDcsId & Field<{
  featureValues: Entries<BundleFeatureValue>;
  bundleSummaryImage: Asset;
  shouldUseCheckoutLink: boolean;
  checkoutLink: ReferencedEntry<ExternalLink>;
  bundleSummaryFeatureValues: Entries<BundleSummaryFeatureValue>;
  pricingPageLink: ReferencedEntry<ExternalLink | InternalLink>;
}>;

export type Solution = HasInternalName & HasTitle & Field<{
  bundles: Entries<Bundle>;
  showTag: boolean;
  tagText: string;
  tagBackgroundColor: string;
  bundleWithTag: ReferencedEntry<Bundle>;
}>;

export type SolutionPickerModule = HasInternalName & HasTitle & HasSubTitle & HasBackgroundColor & Field<{
  items: Entries<Subject>;
  isUsedWithEmptyCartPage: boolean;
  variant: string;
}>;

export type ProductModule = HasInternalName & HasDescription & HasBackgroundColor & HasImages & HasDcsId & Field<{
  name: string;
  label: string;
  productFeatures: Entries<ProductFeature>;
  showPrice: boolean;
}>;

export type ProductFeature = HasInternalName & HasTitle & Field<{
  value: string;
}>;

export type StickyHeaderAssembly = HasInternalName & HasTitle & HasBackgroundColor & Field<{
  items: Entries<Product>;
}>;

export type SideBySideAssembly = CarouselAssembly;

export type AdAgent = HasInternalName & Field<{
  agentId: string;
  name: string;
  logo: string;
  personalizationToken: string;
  partner: string;
}>;

export type ProductSystemsItems = HasInternalName & HasDescription & HasBackgroundColor & HasLink & Field<{
  heading: string;
  headingColor: string;
  descriptionColor: string;
  dcsId: string;
  backgroundImage: Asset;
  backgroundColorContainer: string;
  imageAlignment: ImageAlignment;
}>;

export type ProductSystemsCatalog = HasInternalName & HasTitle & HasBackgroundColor & Field<{
  productSystemItems: Entries<ProductSystemsItems>;
}>;

export type SoftwarePlan = HasInternalName & HasDcsId & Field<{
  name: string;
}>;

// TODO: Expand upon this for contentful
export type HeroModule = HasImage & HasLink & Field<{
  headline: string;
  headlineColor: string;
}>;
